import { DashboardActionTypes } from './constants';
import { DashboardActionType } from './actions';
import { IDashboard } from 'pages/apps/DashboardConfig/types';

export interface DashboardState {
    dashboards: IDashboard[];       // list of root dashboard records
    dashboard: IDashboard | null;   // single full-detail record (with banners + sections)
    loading: boolean;
    error: string | null;
    isSuccess: boolean;             // true after create / update / delete
}

const INIT_STATE: DashboardState = {
    dashboards: [],
    dashboard: null,
    loading: false,
    error: null,
    isSuccess: false,
};

const DashboardReducer = (
    state: DashboardState = INIT_STATE,
    action: DashboardActionType
): DashboardState => {
    switch (action.type) {
        // ─── API_RESPONSE_SUCCESS ────────────────────────────────────────────────
        case DashboardActionTypes.API_RESPONSE_SUCCESS:
            switch ((action.payload as any).actionType) {
                case DashboardActionTypes.GET_DASHBOARDS: {
                    const data = (action.payload as any).data;
                    // Backend returns a single object or an array; normalise to array
                    const list = Array.isArray(data) ? data : data ? [data] : [];
                    return {
                        ...state,
                        dashboards: list,
                        loading: false,
                        error: null,
                        isSuccess: false,
                    };
                }

                case DashboardActionTypes.GET_DASHBOARD_BY_ID: {
                    return {
                        ...state,
                        dashboard: (action.payload as any).data,
                        loading: false,
                        error: null,
                        isSuccess: false,
                    };
                }

                case DashboardActionTypes.CREATE_DASHBOARD: {
                    const created: IDashboard = (action.payload as any).data;
                    return {
                        ...state,
                        dashboards: [created, ...state.dashboards],
                        dashboard: created,
                        loading: false,
                        error: null,
                        isSuccess: true,
                    };
                }

                case DashboardActionTypes.UPDATE_DASHBOARD: {
                    const updated: IDashboard = (action.payload as any).data;
                    return {
                        ...state,
                        dashboards: state.dashboards.map((d) =>
                            d.id === updated.id ? updated : d
                        ),
                        dashboard: updated,
                        loading: false,
                        error: null,
                        isSuccess: true,
                    };
                }

                case DashboardActionTypes.DELETE_DASHBOARD: {
                    const deletedId = (action.payload as any).data;
                    return {
                        ...state,
                        dashboards: state.dashboards.filter((d) => d.id !== deletedId),
                        loading: false,
                        error: null,
                        isSuccess: true,
                    };
                }

                default:
                    return state;
            }

        // ─── API_RESPONSE_ERROR ──────────────────────────────────────────────────
        case DashboardActionTypes.API_RESPONSE_ERROR:
            switch ((action.payload as any).actionType) {
                case DashboardActionTypes.GET_DASHBOARDS:
                case DashboardActionTypes.GET_DASHBOARD_BY_ID:
                case DashboardActionTypes.CREATE_DASHBOARD:
                case DashboardActionTypes.UPDATE_DASHBOARD:
                case DashboardActionTypes.DELETE_DASHBOARD: {
                    return {
                        ...state,
                        loading: false,
                        error: (action.payload as any).error,
                        isSuccess: false,
                    };
                }

                default:
                    return state;
            }

        // ─── Loading triggers ────────────────────────────────────────────────────
        case DashboardActionTypes.GET_DASHBOARDS:
        case DashboardActionTypes.GET_DASHBOARD_BY_ID:
        case DashboardActionTypes.CREATE_DASHBOARD:
        case DashboardActionTypes.UPDATE_DASHBOARD:
        case DashboardActionTypes.DELETE_DASHBOARD: {
            return {
                ...state,
                loading: true,
                error: null,
                isSuccess: false,
            };
        }

        // ─── Reset ───────────────────────────────────────────────────────────────
        case DashboardActionTypes.RESET: {
            return { ...INIT_STATE };
        }

        default:
            return state;
    }
};

export default DashboardReducer;
